import React, { useState } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { Users, LayoutDashboard, User, History, Home, Sun, Moon, AlertCircle, Sparkles, Menu, X, UserPlus, Globe, Rocket } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `group relative flex items-center px-4 py-2 text-sm font-medium transition-all ${
      isActive
        ? 'text-black dark:text-white'
        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
    }`;

  const mobileNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center px-4 py-3 text-sm font-medium transition-all ${
      isActive
        ? 'text-black dark:text-white bg-gray-100 dark:bg-gray-800'
        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
    }`;

  const getIndicatorClass = ({ isActive }: { isActive: boolean }) =>
    `absolute -bottom-[1px] left-1/2 h-[2px] transform -translate-x-1/2 transition-all duration-200 ease-out ${
      isActive 
        ? 'w-12 bg-gradient-to-r from-black/5 via-black to-black/5 dark:from-white/5 dark:via-white dark:to-white/5' 
        : 'w-0 group-hover:w-8 group-hover:bg-gradient-to-r group-hover:from-gray-300/0 group-hover:via-gray-300 group-hover:to-gray-300/0 dark:group-hover:from-gray-600/0 dark:group-hover:via-gray-600 dark:group-hover:to-gray-600/0'
    }`;

  // 获取当前页面标题
  const getPageTitle = () => {
    switch (location.pathname) {
      case '/':
        return '首页';
      case '/workbench':
        return '工作台';
      case '/customers':
        return '我的客户';
      case '/roadmap':
        return '产品路线';
      case '/profile':
        return '个人中心';
      case '/training':
        return 'AI外贸训练营';
      case '/about':
        return '关于我们';
      default:
        return '';
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-gray-100 bg-white/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-950/80">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <span className="text-2xl font-bold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-[#0066FF] to-[#0AC2FF]">
                Sohobot
              </span>
            </Link>
            <div className="hidden md:block ml-10">
              <div className="flex items-center space-x-4">
                <NavLink to="/" className={navLinkClass} end>
                  {({ isActive }) => (
                    <>
                      <div className="flex items-center">
                        <Home className="w-4 h-4 mr-2" />
                        首页
                      </div>
                      <div className={getIndicatorClass({ isActive })} />
                    </>
                  )}
                </NavLink>
                <NavLink to="/workbench" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <div className="flex items-center">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        工作台
                      </div>
                      <div className={getIndicatorClass({ isActive })} />
                    </>
                  )}
                </NavLink>
                <NavLink to="/customers" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-2" />
                        我的客户
                      </div>
                      <div className={getIndicatorClass({ isActive })} />
                    </>
                  )}
                </NavLink>
                <NavLink to="/training" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <div className="flex items-center">
                        <Rocket className="w-4 h-4 mr-2" />
                        AI外贸训练营
                      </div>
                      <div className={getIndicatorClass({ isActive })} />
                    </>
                  )}
                </NavLink>
                <NavLink to="/roadmap" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <div className="flex items-center">
                        <History className="w-4 h-4 mr-2" />
                        产品路线
                      </div>
                      <div className={getIndicatorClass({ isActive })} />
                    </>
                  )}
                </NavLink>
                <NavLink to="/about" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <div className="flex items-center">
                        <Sparkles className="w-4 h-4 mr-2" />
                        关于我们
                      </div>
                      <div className={getIndicatorClass({ isActive })} />
                    </>
                  )}
                </NavLink>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* 当前页面标题 */}
            <div className="hidden md:block">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {getPageTitle()}
              </span>
            </div>
            <div className="hidden md:block h-4 w-px bg-gray-200 dark:bg-gray-700" />
            {/* 主题切换按钮 */}
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              aria-label="切换主题"
            >
              {isDark ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
            <div className="hidden md:block h-4 w-px bg-gray-200 dark:bg-gray-700" />
            {/* 桌面端导航 */}
            <div className="hidden md:block">
              <div className="flex items-center space-x-4">
                {user ? (
                  <div className="flex items-center">
                    <NavLink to="/profile" className={navLinkClass}>
                      {({ isActive }) => (
                        <>
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-2" />
                            {user.username}
                            {(!user.company || !user.companyInfo) && (
                              <div className="relative ml-2 group">
                                <AlertCircle className="w-4 h-4 text-amber-500" />
                                <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 px-3 py-1 
                                  bg-black dark:bg-white text-white dark:text-black text-xs rounded-lg opacity-0 
                                  group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                  请完善企业信息
                                </div>
                              </div>
                            )}
                          </div>
                          <div className={getIndicatorClass({ isActive })} />
                        </>
                      )}
                    </NavLink>
                    <div className="flex items-center">
                      <button
                        onClick={async () => {
                          await logout();
                          navigate('/login');
                        }}
                        className="ml-4 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                      >
                        退出
                      </button>
                    </div>
                  </div>
                ) : (
                  <NavLink to="/login" className={({ isActive }) => 
                    `group relative flex items-center px-4 py-2 text-sm font-medium transition-all ${
                      isActive
                        ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#0066FF] to-[#0AC2FF]'
                        : 'text-transparent bg-clip-text bg-gradient-to-r from-[#0066FF] to-[#0AC2FF] hover:from-[#0052CC] hover:to-[#0099FF]'
                    }`
                  }>
                    {({ isActive }) => (
                      <>
                        <div className="flex items-center">
                          <UserPlus className="w-4 h-4 mr-2 text-[#0066FF]" />
                          登录
                        </div>
                        <div className={getIndicatorClass({ isActive })} />
                      </>
                    )}
                  </NavLink>
                )}
              </div>
            </div>

            {/* 移动端菜单按钮 */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* 移动端导航菜单 */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950">
          <div className="space-y-1 px-2 pb-3 pt-2">
            <NavLink to="/" className={mobileNavLinkClass} end onClick={() => setIsMenuOpen(false)}>
              <Home className="w-4 h-4 mr-2" />
              首页
            </NavLink>
            <NavLink to="/workbench" className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)}>
              <LayoutDashboard className="w-4 h-4 mr-2" />
              工作台
            </NavLink>
            <NavLink to="/customers" className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)}>
              <Users className="w-4 h-4 mr-2" />
              我的客户
            </NavLink>
            <NavLink to="/training" className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)}>
              <Rocket className="w-4 h-4 mr-2" />
              AI外贸训练营
            </NavLink>
            <NavLink to="/roadmap" className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)}>
              <History className="w-4 h-4 mr-2" />
              产品路线
            </NavLink>
            <NavLink to="/about" className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)}>
              <Sparkles className="w-4 h-4 mr-2" />
              关于我们
            </NavLink>
            {user ? (
              <>
                <NavLink to="/profile" className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)}>
                  <User className="w-4 h-4 mr-2" />
                  {user.username}
                  {(!user.company || !user.companyInfo) && (
                    <AlertCircle className="w-4 h-4 text-amber-500 ml-2" />
                  )}
                </NavLink>
                <button
                  onClick={async () => {
                    await logout();
                    setIsMenuOpen(false);
                    navigate('/login');
                  }}
                  className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  退出
                </button>
              </>
            ) : (
              <NavLink to="/login" className={({ isActive }) => 
                `flex items-center px-4 py-3 text-sm font-medium transition-all ${
                  isActive
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#0066FF] to-[#0AC2FF]'
                    : 'text-transparent bg-clip-text bg-gradient-to-r from-[#0066FF] to-[#0AC2FF]'
                }`
              } onClick={() => setIsMenuOpen(false)}>
                <UserPlus className="w-4 h-4 mr-2 text-[#0066FF]" />
                登录
              </NavLink>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 